import { withTheme } from 'common/styling/theme';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { BaseSelectField, PageContentPreloader } from 'common/components';
import { compose } from 'redux';
import { staticStyles, getDynamicStyles } from './style';

const ClientAnalyticsChart = ({ analyticsChartData, analyticsChartDataIsLoaded, selectedPeriod, theme }) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [selectedField, setSelectedField] = useState('net_profit');

  const fieldOptions = [
    { value: 'net_profit', label: 'Net Profit' },
    { value: 'closed_orders', label: 'Closed Orders' },
    { value: 'trading_volume', label: 'Trading Volume' },
  ];

  const chartData = useMemo(() => {
    const entries = Object.entries(analyticsChartData || {});

    const prepare = values => {
      if (selectedPeriod !== 365) {
        return values;
      }

      const grouped = values.reduce((acc, { date, value }) => {
        const month = date.slice(0, 7);

        acc[month] = (acc[month] || 0) + value;

        return acc;
      }, {});

      return {
        categories: Object.keys(grouped),
        data: Object.values(grouped).map(v => Number(v.toFixed(2))),
      };
    };

    if (selectedField === 'closed_orders') {
      const profitable = prepare(
        entries.map(([date, values]) => ({
          date,
          value: Number(values.profitable_orders || 0),
        }))
      );

      const unprofitable = prepare(
        entries.map(([date, values]) => ({
          date,
          value: Number(values.unprofitable_orders || 0),
        }))
      );

      return {
        categories: profitable.categories || entries.map(([date]) => date),
        series: [
          {
            name: 'Profitable',
            data: profitable.data || profitable.map(i => i.value),
          },
          {
            name: 'Unprofitable',
            data: unprofitable.data || unprofitable.map(i => i.value),
          },
        ],
      };
    }

    const single = prepare(
      entries.map(([date, values]) => ({
        date,
        value: Number(values[selectedField] || 0),
      }))
    );

    return {
      categories: single.categories || entries.map(([date]) => date),
      series: [
        {
          name: fieldOptions.find(item => item.value === selectedField)?.label,
          data: single.data || single.map(i => i.value),
        },
      ],
    };
  }, [analyticsChartData, selectedField, selectedPeriod]);

  const chartSeries = chartData.series;

  const chartOptions = useMemo(
    () => ({
      chart: {
        stacked: selectedField === 'closed_orders',
        zoom: {
          enabled: false,
        },
      },
      colors: selectedField === 'closed_orders' ? ['#389700', '#FF4058'] : undefined,
      stroke: {
        curve: 'smooth',
      },
      plotOptions: {
        bar: {
          columnWidth: '80%',
          borderRadius: 3,
        },
      },
      grid: {
        borderColor: '#f1f1f1',
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            xaxis: {
              tickAmount: 6,
            },
          },
        },
      ],
      dataLabels: {
        enabled: true,
        formatter: val => {
          if (selectedPeriod !== 7) {
            return '';
          }
          return val;
        },
      },
      xaxis: {
        categories: chartData.categories,
        tickAmount: 20,
        labels: {
          rotate: -45,
          rotateAlways: selectedPeriod === 30,
          formatter: value => {
            const date = new Date(value);

            if (selectedPeriod === 365) {
              return date.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
              });
            }

            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
          },
        },
      },
      tooltip: {
        intersect: false,
        shared: true,
        custom: ({ series, dataPointIndex, w }) => {
          const title = w.globals.labels?.[dataPointIndex] ?? w.config.xaxis.categories?.[dataPointIndex] ?? '';

          const isClosedOrders = w.config.series[0].name === 'Profitable';

          const rows = w.config.series
            .map(
              (s, i) => `
        <div style="display:flex;justify-content:space-between;gap:16px">
          <span>${s.name}</span>
          <span>${series[i][dataPointIndex]}</span>
        </div>
      `
            )
            .join('');

          let totalRow = '';

          if (isClosedOrders) {
            const profitable = series[0][dataPointIndex];
            const unprofitable = series[1][dataPointIndex];

            totalRow = `
        <div style="margin-top:6px;padding-top:6px;border-top:1px solid #eee;display:flex;justify-content:space-between;gap:16px">
          <strong>Closed Orders</strong>
          <strong>${profitable + unprofitable}</strong>
        </div>
      `;
          }

          return `
      <div class="apexcharts-tooltip-box" style="padding:8px">
        <div style="font-weight:600;margin-bottom:6px">${title}</div>
        ${rows}
        ${totalRow}
      </div>
    `;
        },
      },
    }),
    [chartData, selectedField, selectedPeriod]
  );

  return (
    <div className="ClientAnalyticsChart">
      <BaseSelectField
        name="metric"
        textId="analyticsMetric"
        value={fieldOptions.find(item => item.value === selectedField)}
        options={fieldOptions}
        onChange={option => setSelectedField(option?.value)}
      />
      <PageContentPreloader ready={analyticsChartDataIsLoaded} type="bigLine">
        {chartSeries[0]?.data?.length > 0 && analyticsChartDataIsLoaded && (
          <div className="ClientAnalyticsChart__chart">
            <ReactApexChart type="bar" height={400} options={chartOptions} series={chartSeries} />
          </div>
        )}
      </PageContentPreloader>

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

ClientAnalyticsChart.propTypes = {
  selectedPeriod: PropTypes.number.isRequired,
  analyticsChartData: PropTypes.object.isRequired,
  analyticsChartDataIsLoaded: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

ClientAnalyticsChart.defaultProps = {
  theme: {},
};

export default compose(withTheme())(ClientAnalyticsChart);
export { ClientAnalyticsChart };
